import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ComposedChart,
  Line,
  PieChart,
  Pie,
  Cell,
} from "recharts";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function FinancesView() {
  const [activeTab, setActiveTab] = useState("Overall");
  const [tableData, setTableData] = useState([]);
  const [gpmChartData, setGpmChartData] = useState([]);
  const [timesheetChartData, setTimesheetChartData] = useState([]);
  const [billsChartData, setBillsChartData] = useState([]);
  const [pieceworkerChartData, setPieceworkerChartData] = useState([]);
  const [contractChartData, setContractChartData] = useState([]);
  const [projectCostChartData, setProjectCostChartData] = useState([]);
  const [projectCostTableData, setProjectCostTableData] = useState([]);
  const [billsData, setBillsData] = useState([]);
  const [vendorPieData, setVendorPieData] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [sovData, setSovData] = useState([]);
  const [arBarData, setArBarData] = useState([]);
  const [arPieData, setArPieData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Colors for charts (consistent with MetricsTab)
  const COLORS = [
    "#4CAF50", // Green
    "#2196F3", // Blue
    "#F44336", // Red
    "#FF9800", // Orange
    "#3F51B5", // Indigo
    "#9C27B0", // Purple
    "#FF5722", // Deep Orange
  ];

  // Custom Tooltip for charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-gray-700 p-3 border border-gray-600 rounded shadow-md">
          <p className="text-sm font-semibold text-gray-200">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-xs text-gray-300">
              {entry.name}:{" "}
              {entry.dataKey.includes("Cost") ||
              entry.dataKey.includes("Invoiced") ||
              entry.dataKey.includes("Amount")
                ? `$${entry.value.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                  })}`
                : `${entry.value.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                  })}%`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const formatNumber = (num) =>
    (num ?? 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  useEffect(() => {
    const fetchFinancesData = async () => {
      try {
        const token = localStorage.getItem("token");
        const currentYear = new Date().getFullYear();
        const currentDate = new Date();

        const [
          projectsResponse,
          sovResponse,
          timesheetResponse,
          pieceworkerResponse,
          billsResponse,
        ] = await Promise.all([
          axios.get(
            `https://erp-production-72da01c8e651.herokuapp.com/api/projects?year=${currentYear}`,
            { headers: { Authorization: `Bearer ${token}` } },
          ),
          axios.get(
            "https://erp-production-72da01c8e651.herokuapp.com/api/sovs?all=true",
            { headers: { Authorization: `Bearer ${token}` } },
          ),
          axios.get(
            "https://erp-production-72da01c8e651.herokuapp.com/api/timesheets?all=true",
            { headers: { Authorization: `Bearer ${token}` } },
          ),
          axios.get(
            "https://erp-production-72da01c8e651.herokuapp.com/api/pieceworkerLogs?all=true",
            { headers: { Authorization: `Bearer ${token}` } },
          ),
          axios.get(
            "https://erp-production-72da01c8e651.herokuapp.com/api/bills?all=true",
            { headers: { Authorization: `Bearer ${token}` } },
          ),
        ]);

        const projects = projectsResponse.data.projects || [];
        const months = [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ];
        const totalInvoiced = new Array(12).fill(0);
        const totalCosts = new Array(12).fill(0);
        const timesheetApproved = new Array(12).fill(0);
        const timesheetPending = new Array(12).fill(0);
        const billsPaid = new Array(12).fill(0);
        const billsUnpaid = new Array(12).fill(0);
        const pieceworkerApproved = new Array(12).fill(0);
        const pieceworkerPending = new Array(12).fill(0);

        let totalInvoicedSum = 0;
        (sovResponse.data.scheduleOfValues || []).forEach((sov) => {
          const date = new Date(sov.createdAt);
          totalInvoicedSum += parseFloat(sov.totalInvoiced || 0);
          if (date.getFullYear() === currentYear) {
            totalInvoiced[date.getMonth()] += parseFloat(
              sov.totalInvoiced || 0,
            );
          }
        });

        (timesheetResponse.data.timesheets || []).forEach((ts) => {
          const date = new Date(ts.start);
          if (date.getFullYear() === currentYear && ts.totalCost) {
            const monthIdx = date.getMonth();
            totalCosts[monthIdx] += parseFloat(ts.totalCost || 0);
            if (
              ts.status === true &&
              ts.clockInStatus === false &&
              ts.Rejected === false
            ) {
              timesheetApproved[monthIdx] += parseFloat(ts.totalCost || 0);
            } else if (
              ts.status === false &&
              ts.Rejected === false &&
              ts.clockInStatus === false
            ) {
              timesheetPending[monthIdx] += parseFloat(ts.totalCost || 0);
            }
          }
        });

        (pieceworkerResponse.data || []).forEach((pw) => {
          const date = new Date(pw.submittedAt || pw.workCompletedDate);
          if (date.getFullYear() === currentYear && pw.totalCost) {
            const monthIdx = date.getMonth();
            totalCosts[monthIdx] += parseFloat(pw.totalCost || 0);
            if (pw.status === "approved") {
              pieceworkerApproved[monthIdx] += parseFloat(pw.totalCost || 0);
            } else if (pw.status === "pending") {
              pieceworkerPending[monthIdx] += parseFloat(pw.totalCost || 0);
            }
          }
        });

        const allBills = (billsResponse.data || []).map((bill) => ({
          ...bill,
          vendorName: bill.payTo?.companyName || "Unassigned",
          daysOutstanding:
            bill.invoiceStatus !== "paid" && !bill.datePaid
              ? Math.floor(
                  (currentDate - new Date(bill.invoiceDate)) /
                    (1000 * 60 * 60 * 24),
                )
              : 0,
        }));

        allBills.forEach((bill) => {
          const date = new Date(bill.invoiceDate);
          if (date.getFullYear() === currentYear && bill.total) {
            const monthIdx = date.getMonth();
            totalCosts[monthIdx] += parseFloat(bill.total || 0);
            if (bill.invoiceStatus === "paid" || bill.datePaid) {
              billsPaid[monthIdx] += parseFloat(bill.total || 0);
            } else {
              billsUnpaid[monthIdx] += parseFloat(bill.total || 0);
            }
          }
        });

        const projectNames = projects.map(
          (p) => p.projectName || "Unnamed Project",
        );
        const totalCostsPerProject = new Array(projects.length).fill(0);
        const pendingCosts = new Array(projects.length).fill(0);
        const estimatedCosts = new Array(projects.length).fill(0);
        const revisedEstimatedCosts = new Array(projects.length).fill(0);

        projects.forEach((project, idx) => {
          const budgets = project.budgets || [];
          const firstBudget = budgets[0];
          estimatedCosts[idx] = firstBudget
            ? (firstBudget.lineItems || []).reduce(
                (sum, item) => sum + (parseFloat(item.amount) || 0),
                0,
              )
            : 0;

          const subsequentBudgets = budgets.slice(1);
          revisedEstimatedCosts[idx] = subsequentBudgets.reduce(
            (sum, budget) => {
              return (
                sum +
                (budget.lineItems || []).reduce(
                  (subSum, item) => subSum + (parseFloat(item.amount) || 0),
                  0,
                )
              );
            },
            0,
          );

          (timesheetResponse.data.timesheets || []).forEach((ts) => {
            if (ts.project?._id?.toString() === project._id.toString()) {
              const cost = parseFloat(ts.totalCost || 0);
              totalCostsPerProject[idx] += cost;
              if (
                ts.status === false &&
                ts.Rejected === false &&
                ts.clockInStatus === false
              ) {
                pendingCosts[idx] += cost;
              }
            }
          });

          (pieceworkerResponse.data || []).forEach((pw) => {
            if (pw.project?._id?.toString() === project._id.toString()) {
              const cost = parseFloat(pw.totalCost || 0);
              totalCostsPerProject[idx] += cost;
              if (pw.status === "pending") {
                pendingCosts[idx] += cost;
              }
            }
          });

          allBills.forEach((bill) => {
            if (bill.project?._id?.toString() === project._id.toString()) {
              const cost = parseFloat(bill.total || 0);
              totalCostsPerProject[idx] += cost;
              if (!(bill.invoiceStatus === "paid" || bill.datePaid)) {
                pendingCosts[idx] += cost;
              }
            }
          });
        });

        const gpm = totalInvoiced.map((invoiced, idx) => {
          const costs = totalCosts[idx];
          return invoiced > 0 ? ((invoiced - costs) / invoiced) * 100 : 0;
        });

        const gpmData = months.map((month, idx) => ({
          month,
          "Total Invoiced": totalInvoiced[idx],
          "Gross Profit Margin": gpm[idx],
          "GPM Goal": 30,
        }));

        const timesheetData = months.map((month, idx) => ({
          month,
          Approved: timesheetApproved[idx],
          Pending: timesheetPending[idx],
        }));

        const billsDataChart = months.map((month, idx) => ({
          month,
          Paid: billsPaid[idx],
          Unpaid: billsUnpaid[idx],
        }));

        const pieceworkerData = months.map((month, idx) => ({
          month,
          Approved: pieceworkerApproved[idx],
          Pending: pieceworkerPending[idx],
        }));

        const totalInvoicedByProject = projects.map(() => 0);
        const contractValues = projects.map(
          (p) =>
            (p.originalContractValue || 0) + (p.changeOrderContractValue || 0),
        );

        (sovResponse.data.scheduleOfValues || []).forEach((sov) => {
          const projectIdx = projects.findIndex(
            (p) => p._id.toString() === sov.project?._id?.toString(),
          );
          if (projectIdx !== -1) {
            totalInvoicedByProject[projectIdx] += parseFloat(
              sov.totalInvoiced || 0,
            );
          }
        });

        const contractData = projectNames.map((name, idx) => ({
          name,
          "Total Invoiced": totalInvoicedByProject[idx],
          "Contract Value": contractValues[idx],
        }));

        const projectCostData = projectNames.map((name, idx) => ({
          name,
          "Total Costs": totalCostsPerProject[idx],
          "Pending Costs": pendingCosts[idx],
          "Estimated Cost": estimatedCosts[idx],
          "Revised Estimated Cost": revisedEstimatedCosts[idx],
        }));

        const projectCostTableRows = projects.map((project, idx) => ({
          projectName: projectNames[idx],
          totalCost: totalCostsPerProject[idx].toFixed(2),
          pendingCost: pendingCosts[idx].toFixed(2),
          estimatedCost: estimatedCosts[idx].toFixed(2),
          revisedEstimatedCost: revisedEstimatedCosts[idx].toFixed(2),
        }));

        const unpaidBills = allBills.filter(
          (bill) => bill.invoiceStatus !== "paid" && !bill.datePaid,
        );
        const vendorTotals = {};
        unpaidBills.forEach((bill) => {
          vendorTotals[bill.vendorName] =
            (vendorTotals[bill.vendorName] || 0) + parseFloat(bill.total || 0);
        });

        const vendorPie = Object.entries(vendorTotals).map(([name, total]) => ({
          name,
          value: total,
        }));

        const allSovs = (sovResponse.data.scheduleOfValues || []).map((sov) => {
          const outstanding =
            parseFloat(sov.totalInvoiced || 0) -
            parseFloat(sov.amountPaid || 0);
          return {
            ...sov,
            companyName:
              sov.project?.billingAccount?.companyName || "Unassigned",
            outstanding: outstanding > 0 ? outstanding : 0,
            daysOutstanding:
              outstanding > 0
                ? Math.floor(
                    (currentDate - new Date(sov.invoiceDate || sov.createdAt)) /
                      (1000 * 60 * 60 * 24),
                  )
                : 0,
          };
        });

        const outstandingSovs = allSovs.filter((sov) => sov.outstanding > 0);
        const arByCompany = {};
        outstandingSovs.forEach((sov) => {
          arByCompany[sov.companyName] =
            (arByCompany[sov.companyName] || 0) + sov.outstanding;
        });

        const arBar = Object.entries(arByCompany).map(
          ([name, outstanding]) => ({
            name,
            "Outstanding Amount": outstanding,
          }),
        );

        const arPie = Object.entries(arByCompany).map(
          ([name, outstanding]) => ({
            name,
            value: outstanding,
          }),
        );

        setTableData(
          months.map((month, idx) => ({
            month,
            totalInvoiced: totalInvoiced[idx].toFixed(2),
            totalCosts: totalCosts[idx].toFixed(2),
            gpm: gpm[idx].toFixed(2),
          })),
        );
        setGpmChartData(gpmData);
        setTimesheetChartData(timesheetData);
        setBillsChartData(billsDataChart);
        setPieceworkerChartData(pieceworkerData);
        setContractChartData(contractData);
        setProjectCostChartData(projectCostData);
        setProjectCostTableData(projectCostTableRows);
        setBillsData(unpaidBills);
        setVendorPieData(vendorPie);
        setSovData(outstandingSovs);
        setArBarData(arBar);
        setArPieData(arPie);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching finances data:", err);
        setError(
          "Failed to load finances data: " +
            (err.response?.data?.message || err.message),
        );
        setLoading(false);
      }
    };

    fetchFinancesData();
  }, []);

  const currentYear = new Date().getFullYear();

  const filterBillsByDays = (bills, minDays, maxDays) =>
    bills.filter(
      (bill) =>
        (!selectedVendor || bill.vendorName === selectedVendor) &&
        bill.daysOutstanding >= minDays &&
        (maxDays ? bill.daysOutstanding < maxDays : true),
    );

  const calculateOutstandingByRange = (sovs, minDays, maxDays) =>
    sovs.reduce(
      (sum, sov) =>
        sov.daysOutstanding >= minDays &&
        (maxDays ? sov.daysOutstanding < maxDays : true)
          ? sum + sov.outstanding
          : sum,
      0,
    );

  if (loading) {
    return (
      <div className="text-center py-4 text-gray-200">Loading finances...</div>
    );
  }

  if (error) {
    return <div className="text-red-400 text-center py-4">{error}</div>;
  }

  const tabs = ["Overall", "Accounts Payable", "Accounts Receivable"];

  const BillTable = ({ title, bills }) => {
    return (
      <div className="bg-gray-700 p-4 rounded-lg shadow mb-4">
        <h3 className="text-md font-semibold text-gray-200 mb-2">{title}</h3>
        <div className="overflow-auto" style={{ maxHeight: "300px" }}>
          <table className="min-w-full divide-y divide-gray-600">
            <thead className="bg-gray-800 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Vendor
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Invoice #
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Total ($)
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Days Outstanding
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-600">
              {bills.map((bill, idx) => (
                <tr
                  key={bill._id}
                  className={idx % 2 === 0 ? "bg-gray-700" : "bg-gray-600"}
                >
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                    {bill.vendorName}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                    {bill.invoiceNumber || "N/A"}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                    {new Date(bill.invoiceDate).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                    {formatNumber(parseFloat(bill.total || 0))}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                    {bill.daysOutstanding}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const ARTable = () => {
    const totals = {
      outstanding: sovData.reduce((sum, sov) => sum + sov.outstanding, 0),
      days1_30: calculateOutstandingByRange(sovData, 0, 31),
      days31_60: calculateOutstandingByRange(sovData, 31, 61),
      days61_90: calculateOutstandingByRange(sovData, 61, 91),
      days90Plus: calculateOutstandingByRange(sovData, 91, null),
    };

    return (
      <div className="bg-gray-700 p-4 rounded-lg shadow col-span-2">
        <h2 className="text-lg font-semibold text-gray-200 mb-2">
          Accounts Receivable Summary
        </h2>
        <div className="overflow-auto" style={{ maxHeight: "400px" }}>
          <table className="min-w-full divide-y divide-gray-600">
            <thead className="bg-gray-800 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Client Name
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Outstanding ($)
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  1-30 Days ($)
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  31-60 Days ($)
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  61-90 Days ($)
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  90+ Days ($)
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-600">
              {Object.entries(
                sovData.reduce((acc, sov) => {
                  acc[sov.companyName] = acc[sov.companyName] || {
                    outstanding: 0,
                    days1_30: 0,
                    days31_60: 0,
                    days61_90: 0,
                    days90Plus: 0,
                  };
                  acc[sov.companyName].outstanding += sov.outstanding;
                  if (sov.daysOutstanding < 31)
                    acc[sov.companyName].days1_30 += sov.outstanding;
                  else if (sov.daysOutstanding < 61)
                    acc[sov.companyName].days31_60 += sov.outstanding;
                  else if (sov.daysOutstanding < 91)
                    acc[sov.companyName].days61_90 += sov.outstanding;
                  else acc[sov.companyName].days90Plus += sov.outstanding;
                  return acc;
                }, {}),
              ).map(([companyName, data], idx) => (
                <tr
                  key={companyName}
                  className={idx % 2 === 0 ? "bg-gray-700" : "bg-gray-600"}
                >
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                    {companyName}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                    {formatNumber(data.outstanding)}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                    {formatNumber(data.days1_30)}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                    {formatNumber(data.days31_60)}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                    {formatNumber(data.days61_90)}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                    {formatNumber(data.days90Plus)}
                  </td>
                </tr>
              ))}
              <tr className="font-bold bg-gray-600">
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                  Total
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                  {formatNumber(totals.outstanding)}
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                  {formatNumber(totals.days1_30)}
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                  {formatNumber(totals.days31_60)}
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                  {formatNumber(totals.days61_90)}
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                  {formatNumber(totals.days90Plus)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const ProjectCostTable = () => {
    return (
      <div className="bg-gray-700 p-4 rounded-lg shadow col-span-2">
        <h2 className="text-lg font-semibold text-gray-200 mb-2">
          Project Cost Summary
        </h2>
        <div className="overflow-auto" style={{ maxHeight: "400px" }}>
          <table className="min-w-full divide-y divide-gray-600">
            <thead className="bg-gray-800 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Project Name
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Total Cost ($)
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Pending Cost ($)
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Estimated Cost ($)
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Revised Estimated Cost ($)
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-600">
              {projectCostTableData.map((row, idx) => (
                <tr
                  key={idx}
                  className={idx % 2 === 0 ? "bg-gray-700" : "bg-gray-600"}
                >
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                    {row.projectName}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                    {formatNumber(row.totalCost)}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                    {formatNumber(row.pendingCost)}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                    {formatNumber(row.estimatedCost)}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                    {formatNumber(row.revisedEstimatedCost)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="p-4 bg-gray-200 rounded-lg mt-6">
      <div className="bg-gray-700 rounded-lg p-4 mb-4">
        <h1 className="text-2xl font-bold text-white mb-4">
          Company Finances Overview
        </h1>
        <div className="mb-4">
          <nav className="flex space-x-4" aria-label="Tabs">
            {tabs.map((tab) => (
              <button
                key={tab}
                className={classNames(
                  activeTab === tab
                    ? "bg-orange-500 text-white font-semibold"
                    : "text-gray-400 hover:text-gray-200 hover:bg-orange-400 hover:opacity-90",
                  "rounded-md px-3 py-2 text-sm font-medium",
                )}
                onClick={() => setActiveTab(tab)}
              >
                {tab}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {activeTab === "Overall" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div
            className="bg-gray-700 p-4 rounded-lg shadow"
            style={{ height: "400px" }}
          >
            <h3 className="text-md font-semibold text-gray-200 mb-4">
              Revenue and GPM ({currentYear})
            </h3>
            {gpmChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={400}>
                <ComposedChart
                  data={gpmChartData}
                  margin={{ top: 20, right: 20, bottom: 100, left: 20 }}
                >
                  <XAxis
                    dataKey="month"
                    angle={-45}
                    textAnchor="end"
                    interval={0}
                    height={60}
                    className="text-sm"
                    stroke="#e5e7eb"
                    fontSize={12}
                  />
                  <YAxis
                    yAxisId="left"
                    label={{
                      value: "Revenue ($)",
                      angle: -90,
                      position: "insideLeft",
                      fill: "#e5e7eb",
                      fontSize: 12,
                      dx: -10,
                    }}
                    padding={{ top: 20 }}
                    className="text-sm"
                    stroke="#e5e7eb"
                    fontSize={12}
                    tickFormatter={(value) => value.toLocaleString("en-US")}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    label={{
                      value: "GPM (%)",
                      angle: -90,
                      position: "insideRight",
                      fill: "#e5e7eb",
                      fontSize: 12,
                    }}
                    className="text-sm"
                    stroke="#e5e7eb"
                    fontSize={12}
                    tickFormatter={(value) => value.toLocaleString("en-US")}
                    domain={[0, 100]}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend
                    verticalAlign="bottom"
                    align="center"
                    wrapperStyle={{ fontSize: "12px", paddingTop: "20px" }}
                  />
                  <Bar
                    yAxisId="left"
                    dataKey="Total Invoiced"
                    name="Total Invoiced ($)"
                    fill={COLORS[1]}
                    radius={[12, 12, 0, 0]}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="Gross Profit Margin"
                    name="Gross Profit Margin (%)"
                    stroke={COLORS[2]}
                    strokeWidth={2}
                    dot={{ r: 4, stroke: COLORS[2] }}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="GPM Goal"
                    name="GPM Goal (30%)"
                    stroke={COLORS[0]}
                    strokeDasharray="5 5"
                    strokeWidth={3}
                    dot={{ r: 4, stroke: COLORS[0] }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-gray-400 text-center text-sm">
                No revenue data available
              </p>
            )}
          </div>
          <div
            className="bg-gray-700 p-4 rounded-lg shadow"
            style={{ height: "400px" }}
          >
            <h3 className="text-md font-semibold text-gray-200 mb-4">
              Total Invoiced vs. Contract by Project
            </h3>
            {contractChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={400}>
                <BarChart
                  data={contractChartData}
                  margin={{ top: 20, right: 20, bottom: 100, left: 20 }}
                >
                  <XAxis
                    dataKey="name"
                    angle={-45}
                    textAnchor="end"
                    interval={0}
                    height={60}
                    className="text-sm"
                    stroke="#e5e7eb"
                    fontSize={12}
                    padding={{ right: 50, left: 100 }}
                  />
                  <YAxis
                    label={{
                      value: "Amount ($)",
                      angle: -90,
                      position: "insideLeft",
                      fill: "#e5e7eb",
                      fontSize: 12,
                      dx: -10,
                    }}
                    padding={{ top: 30 }}
                    className="text-sm"
                    stroke="#e5e7eb"
                    fontSize={12}
                    tickFormatter={(value) => value.toLocaleString("en-US")}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend
                    verticalAlign="bottom"
                    align="center"
                    wrapperStyle={{ fontSize: "12px", paddingTop: "20px" }}
                  />
                  <Bar
                    dataKey="Total Invoiced"
                    name="Total Invoiced ($)"
                    fill={COLORS[1]}
                    stackId="invoiced"
                    radius={[12, 12, 0, 0]}
                  />
                  <Bar
                    dataKey="Contract Value"
                    name="Contract Value ($)"
                    fill={COLORS[0]}
                    stackId="contract"
                    radius={[12, 12, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-gray-400 text-center text-sm">
                No contract data available
              </p>
            )}
          </div>
          <div
            className="bg-gray-700 p-4 rounded-lg shadow"
            style={{ height: "400px" }}
          >
            <h3 className="text-md font-semibold text-gray-200 mb-4">
              Timesheet Costs ({currentYear})
            </h3>
            {timesheetChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={400}>
                <BarChart
                  data={timesheetChartData}
                  margin={{ top: 20, right: 20, bottom: 100, left: 20 }}
                >
                  <XAxis
                    dataKey="month"
                    angle={-45}
                    textAnchor="end"
                    interval={0}
                    height={60}
                    className="text-sm"
                    stroke="#e5e7eb"
                    fontSize={12}
                  />
                  <YAxis
                    label={{
                      value: "Costs ($)",
                      angle: -90,
                      position: "insideLeft",
                      fill: "#e5e7eb",
                      fontSize: 12,
                      dx: -10,
                    }}
                    padding={{ top: 20 }}
                    className="text-sm"
                    stroke="#e5e7eb"
                    fontSize={12}
                    tickFormatter={(value) => value.toLocaleString("en-US")}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend
                    verticalAlign="bottom"
                    align="center"
                    wrapperStyle={{ fontSize: "12px", paddingTop: "20px" }}
                  />
                  <Bar
                    dataKey="Approved"
                    name="Approved ($)"
                    fill={COLORS[0]}
                    stackId="timesheet"
                    radius={[12, 12, 0, 0]}
                  />
                  <Bar
                    dataKey="Pending"
                    name="Pending ($)"
                    fill={COLORS[1]}
                    stackId="timesheet"
                    radius={[12, 12, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-gray-400 text-center text-sm">
                No timesheet data available
              </p>
            )}
          </div>
          <div
            className="bg-gray-700 p-4 rounded-lg shadow"
            style={{ height: "400px" }}
          >
            <h3 className="text-md font-semibold text-gray-200 mb-4">
              Bills Costs ({currentYear})
            </h3>
            {billsChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={400}>
                <BarChart
                  data={billsChartData}
                  margin={{ top: 20, right: 20, bottom: 100, left: 20 }}
                >
                  <XAxis
                    dataKey="month"
                    angle={-45}
                    textAnchor="end"
                    interval={0}
                    height={60}
                    className="text-sm"
                    stroke="#e5e7eb"
                    fontSize={12}
                  />
                  <YAxis
                    label={{
                      value: "Costs ($)",
                      angle: -90,
                      position: "insideLeft",
                      fill: "#e5e7eb",
                      fontSize: 12,
                      dx: -10,
                    }}
                    padding={{ top: 20 }}
                    className="text-sm"
                    stroke="#e5e7eb"
                    fontSize={12}
                    tickFormatter={(value) => value.toLocaleString("en-US")}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend
                    verticalAlign="bottom"
                    align="center"
                    wrapperStyle={{ fontSize: "12px", paddingTop: "20px" }}
                  />
                  <Bar
                    dataKey="Paid"
                    name="Paid ($)"
                    fill={COLORS[3]}
                    stackId="bills"
                    radius={[12, 12, 0, 0]}
                  />
                  <Bar
                    dataKey="Unpaid"
                    name="Unpaid ($)"
                    fill={COLORS[4]}
                    stackId="bills"
                    radius={[12, 12, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-gray-400 text-center text-sm">
                No bills data available
              </p>
            )}
          </div>
          <div
            className="bg-gray-700 p-4 rounded-lg shadow"
            style={{ height: "400px" }}
          >
            <h3 className="text-md font-semibold text-gray-200 mb-4">
              Pieceworker Costs ({currentYear})
            </h3>
            {pieceworkerChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={400}>
                <BarChart
                  data={pieceworkerChartData}
                  margin={{ top: 20, right: 20, bottom: 100, left: 20 }}
                >
                  <XAxis
                    dataKey="month"
                    angle={-45}
                    textAnchor="end"
                    interval={0}
                    height={60}
                    className="text-sm"
                    stroke="#e5e7eb"
                    fontSize={12}
                  />
                  <YAxis
                    label={{
                      value: "Costs ($)",
                      angle: -90,
                      position: "insideLeft",
                      fill: "#e5e7eb",
                      fontSize: 12,
                      dx: -10,
                    }}
                    padding={{ top: 20 }}
                    className="text-sm"
                    stroke="#e5e7eb"
                    fontSize={12}
                    tickFormatter={(value) => value.toLocaleString("en-US")}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend
                    verticalAlign="bottom"
                    align="center"
                    wrapperStyle={{ fontSize: "12px", paddingTop: "20px" }}
                  />
                  <Bar
                    dataKey="Approved"
                    name="Approved ($)"
                    fill={COLORS[5]}
                    stackId="pieceworker"
                    radius={[12, 12, 0, 0]}
                  />
                  <Bar
                    dataKey="Pending"
                    name="Pending ($)"
                    fill={COLORS[6]}
                    stackId="pieceworker"
                    radius={[12, 12, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-gray-400 text-center text-sm">
                No pieceworker data available
              </p>
            )}
          </div>
          <div
            className="bg-gray-700 p-4 rounded-lg shadow"
            style={{ height: "400px" }}
          >
            <h3 className="text-md font-semibold text-gray-200 mb-4">
              Total vs. Pending vs. Estimated Costs by Project
            </h3>
            {projectCostChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={400}>
                <ComposedChart
                  data={projectCostChartData}
                  margin={{ top: 20, right: 20, bottom: 100, left: 20 }}
                >
                  <XAxis
                    dataKey="name"
                    angle={-45}
                    textAnchor="end"
                    interval={0}
                    height={60}
                    className="text-sm"
                    stroke="#e5e7eb"
                    fontSize={12}
                  />
                  <YAxis
                    label={{
                      value: "Costs ($)",
                      angle: -90,
                      position: "insideLeft",
                      fill: "#e5e7eb",
                      fontSize: 12,
                      dx: -10,
                    }}
                    padding={{ top: 20 }}
                    className="text-sm"
                    stroke="#e5e7eb"
                    fontSize={12}
                    tickFormatter={(value) => value.toLocaleString("en-US")}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend
                    verticalAlign="bottom"
                    align="center"
                    wrapperStyle={{ fontSize: "12px", paddingTop: "20px" }}
                  />
                  <Bar
                    dataKey="Total Costs"
                    name="Total Costs ($)"
                    fill={COLORS[0]}
                    stackId="costs"
                    radius={[12, 12, 0, 0]}
                  />
                  <Bar
                    dataKey="Pending Costs"
                    name="Pending Costs ($)"
                    fill={COLORS[1]}
                    stackId="costs"
                    radius={[12, 12, 0, 0]}
                  />
                  <Line
                    type="monotone"
                    dataKey="Estimated Cost"
                    name="Estimated Cost ($)"
                    stroke={COLORS[2]}
                    strokeWidth={2}
                    dot={{ r: 4, stroke: COLORS[2] }}
                  />
                  <Line
                    type="monotone"
                    dataKey="Revised Estimated Cost"
                    name="Revised Estimated Cost ($)"
                    stroke={COLORS[3]}
                    strokeWidth={2}
                    dot={{ r: 4, stroke: COLORS[3] }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-gray-400 text-center text-sm">
                No cost data available
              </p>
            )}
          </div>
          <div className="bg-gray-700 p-4 rounded-lg shadow col-span-2 overflow-auto">
            <h2 className="text-lg font-semibold text-gray-200 mb-2">
              Monthly Summary ({currentYear})
            </h2>
            <table className="min-w-full divide-y divide-gray-600">
              <thead className="bg-gray-800 sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Month
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Invoiced ($)
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Costs ($)
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    GPM (%)
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-600">
                {tableData.map((row, idx) => (
                  <tr
                    key={idx}
                    className={idx % 2 === 0 ? "bg-gray-700" : "bg-gray-600"}
                  >
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                      {row.month}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                      {formatNumber(row.totalInvoiced)}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                      {formatNumber(row.totalCosts)}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                      {row.gpm}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ProjectCostTable />
        </div>
      )}

      {activeTab === "Accounts Payable" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div
            className="bg-gray-700 p-4 rounded-lg shadow"
            style={{ height: "400px" }}
          >
            <h3 className="text-md font-semibold text-gray-200 mb-4">
              Outstanding Bills by Vendor
            </h3>
            {vendorPieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={400}>
                <PieChart>
                  <Pie
                    data={vendorPieData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    onClick={(data, index) => {
                      const vendor = vendorPieData[index].name;
                      setSelectedVendor(
                        vendor === selectedVendor ? null : vendor,
                      );
                    }}
                  >
                    {vendorPieData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend
                    verticalAlign="bottom"
                    align="center"
                    wrapperStyle={{ fontSize: "12px", paddingTop: "20px" }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-gray-400 text-center text-sm">
                No vendor data available
              </p>
            )}
          </div>
          <div className="flex flex-col gap-4">
            <BillTable
              title="30 Days Outstanding"
              bills={filterBillsByDays(billsData, 0, 30)}
            />
            <BillTable
              title="60 Days Outstanding"
              bills={filterBillsByDays(billsData, 30, 60)}
            />
            <BillTable
              title="90+ Days Outstanding"
              bills={filterBillsByDays(billsData, 60, null)}
            />
          </div>
        </div>
      )}

      {activeTab === "Accounts Receivable" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div
            className="bg-gray-700 p-4 rounded-lg shadow"
            style={{ height: "400px" }}
          >
            <h3 className="text-md font-semibold text-gray-200 mb-4">
              Outstanding Receivables by Company
            </h3>
            {arBarData.length > 0 ? (
              <ResponsiveContainer width="100%" height={400}>
                <BarChart
                  data={arBarData}
                  layout="vertical"
                  margin={{ top: 20, right: 20, bottom: 100, left: 100 }}
                >
                  <XAxis
                    type="number"
                    label={{
                      value: "Amount ($)",
                      position: "insideBottom",
                      offset: -10,
                      fill: "#e5e7eb",
                      fontSize: 12,
                    }}
                    className="text-sm"
                    stroke="#e5e7eb"
                    fontSize={12}
                    tickFormatter={(value) => value.toLocaleString("en-US")}
                  />
                  <YAxis
                    dataKey="name"
                    type="category"
                    className="text-sm"
                    stroke="#e5e7eb"
                    fontSize={12}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend
                    verticalAlign="bottom"
                    align="center"
                    wrapperStyle={{ fontSize: "12px", paddingTop: "20px" }}
                  />
                  <Bar
                    dataKey="Outstanding Amount"
                    name="Outstanding Amount ($)"
                    fill={COLORS[1]}
                    radius={[12, 12, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-gray-400 text-center text-sm">
                No receivables data available
              </p>
            )}
          </div>
          <div
            className="bg-gray-700 p-4 rounded-lg shadow"
            style={{ height: "400px" }}
          >
            <h3 className="text-md font-semibold text-gray-200 mb-4">
              Outstanding Receivables Breakdown
            </h3>
            {arPieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={400}>
                <PieChart>
                  <Pie
                    data={arPieData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                  >
                    {arPieData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend
                    verticalAlign="bottom"
                    align="center"
                    wrapperStyle={{ fontSize: "12px", paddingTop: "20px" }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-gray-400 text-center text-sm">
                No receivables data available
              </p>
            )}
          </div>
          <ARTable />
        </div>
      )}
    </div>
  );
}
